/**
 * Дни рождения из контактов телефона — ради возраста.
 *
 * Календарь дней рождения показывает сам праздник, но не год: ежегодная серия
 * в нём часто начинается с условного года (1970 и подобных), и возраст из неё
 * вышел бы неверным. Год рождения есть в самом контакте — если человек его
 * указал. Отсюда возраст и берётся: «Маше исполняется 36».
 *
 * Контакты читаются только с разрешения, которое человек даёт отдельно, в
 * настройках календаря: ради цифры возле дня рождения просить доступ ко всей
 * записной книжке без спроса нельзя.
 */

import { Capacitor, type PermissionState, registerPlugin } from '@capacitor/core';

import type { IsoDate } from '@/shared/lib/datetime';

import type { CalendarAccess } from './system-calendar';

/** День рождения так, как его хранит контакт. */
export interface ContactBirthday {
  name: string;
  /** «1990-10-06», «--10-06» без года, иногда «19901006». */
  date: string;
}

export interface ContactsPort {
  available(): boolean;
  checkAccess(): Promise<CalendarAccess>;
  requestAccess(): Promise<CalendarAccess>;
  listBirthdays(): Promise<ContactBirthday[]>;
}

/** Дата рождения: год, если указан, и день в году. `null` — не разобрать. */
export function parseBirthday(raw: string): { year: number | null; monthDay: string } | null {
  const full = /^(\d{4})-?(\d{2})-?(\d{2})/.exec(raw.trim());
  if (full) return { year: Number(full[1]), monthDay: `${full[2]}-${full[3]}` };

  const noYear = /^--(\d{2})-?(\d{2})/.exec(raw.trim());
  if (noYear) return { year: null, monthDay: `${noYear[1]}-${noYear[2]}` };

  return null;
}

const normalize = (text: string) => text.toLowerCase().replaceAll('ё', 'е');

/**
 * Назван ли человек в названии события.
 *
 * Название бывает и просто именем, и фразой: «День рождения Маши Ивановой».
 * Поэтому каждое слово имени ищется по основе — без последней буквы, — и
 * «Маша Иванова» узнаётся в «Маши Ивановой».
 */
function namedIn(title: string, name: string): boolean {
  const words = normalize(title)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  const parts = normalize(name)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  if (parts.length === 0) return false;

  return parts.every((part) => {
    const stem = part.length > 3 ? part.slice(0, -1) : part;
    return words.some((word) => word.startsWith(stem));
  });
}

/**
 * Сколько исполняется в этот день рождения. `null` — неизвестно: в контакте
 * нет года или контакт не нашёлся.
 */
export function birthdayAge(
  item: { date: IsoDate; title: string },
  contacts: readonly ContactBirthday[],
): number | null {
  const monthDay = item.date.slice(5);
  const year = Number(item.date.slice(0, 4));

  for (const contact of contacts) {
    const birthday = parseBirthday(contact.date);
    if (!birthday || birthday.year === null || birthday.monthDay !== monthDay) continue;
    if (!namedIn(item.title, contact.name)) continue;

    const age = year - birthday.year;
    return age >= 1 && age <= 130 ? age : null;
  }

  return null;
}

/* ─────────────────────────── Телефон ─────────────────────────── */

interface KairoContactsPlugin {
  checkPermissions(): Promise<{ contacts: PermissionState }>;
  requestPermissions(): Promise<{ contacts: PermissionState }>;
  birthdays(): Promise<{ birthdays: ContactBirthday[] }>;
}

/**
 * Свой маленький плагин приложения (`KairoContactsPlugin.java`): готовые
 * плагины контактов тянут всю записную книжку, а нужны только дни рождения.
 *
 * Завёрнут, как и плагин календаря: интерфейс плагина отвечает и на `then`, и
 * вернуть его из асинхронной функции как есть нельзя.
 */
function load(): { plugin: KairoContactsPlugin } | null {
  if (Capacitor.getPlatform() !== 'android') return null;
  return { plugin: registerPlugin<KairoContactsPlugin>('KairoContacts') };
}

function toAccess(state: PermissionState | undefined): CalendarAccess {
  if (state === 'granted') return 'granted';
  if (state === 'denied') return 'denied';
  return 'prompt';
}

export const nativeContacts: ContactsPort = {
  available: () => Capacitor.getPlatform() === 'android',

  async checkAccess() {
    const loaded = load();
    if (!loaded) return 'unavailable';
    return toAccess((await loaded.plugin.checkPermissions()).contacts);
  },

  async requestAccess() {
    const loaded = load();
    if (!loaded) return 'unavailable';
    return toAccess((await loaded.plugin.requestPermissions()).contacts);
  },

  async listBirthdays() {
    const loaded = load();
    if (!loaded) return [];
    return (await loaded.plugin.birthdays()).birthdays;
  },
};

export const unavailableContacts: ContactsPort = {
  available: () => false,
  checkAccess: () => Promise.resolve('unavailable'),
  requestAccess: () => Promise.resolve('unavailable'),
  listBirthdays: () => Promise.resolve([]),
};

/** Контакты телефона, если они есть; иначе честная заглушка. */
export function systemContacts(): ContactsPort {
  return Capacitor.getPlatform() === 'android' ? nativeContacts : unavailableContacts;
}
